import { LocalizedRichText, LocalizedText } from "@/i18n/LocalizedText";
import { useLocale } from "@/i18n/LocaleProvider";
import {
  ArrowUpRight,
  ChartNoAxesColumnIncreasing,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Eye,
  Flag,
} from "lucide-react";
import { Badge } from "./ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import { Button } from "./ui/button";
import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { SimpleTable } from "./SimpleTable";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { ExtendedTable } from "./ExtendedTable";
import {
  citedDomainsColumns,
  partialDomains,
  partialPromptsDetails,
  type PartialData,
} from "@/services/helpers";
import {
  getCoreRowModel,
  getPaginationRowModel,
  type ColumnDef,
  useReactTable,
} from "@tanstack/react-table";
import { Separator } from "./ui/separator";
import type { Brand } from "./AppSidebar";
import type { StrategyPlanTask, StrategySnapshotData } from "@/services/api";
import { Skeleton } from "./ui/skeleton";
import { Spinner } from "./ui/spinner";
import { canonicalizeDomain } from "@/utils/domain";
import {
  getDomainTypeBadgeClassName,
  getUrlTypeBadgeClassName,
  toUrlTypeLabel,
} from "@/lib/urlTypes";

import arrow from "../assets/Icon.svg";

export type StrategyProps = {
  currentBrand: Brand;
  selectedCluster: {
    id?: string;
    title?: string;
    prompts?: Array<{
      id?: string | number;
      visibility?: number | null;
      position?: number | null;
    }>;
  } | null;
  snapshot: StrategySnapshotData | null;
  planTasks: StrategyPlanTask[];
  isPlanLoading: boolean;
  isFullVisibilityCluster?: boolean;
  isPdfExporting?: boolean;
  onTasksSectionSeen?: () => void;
};

const STRATEGY_ACCORDION_ITEM_VALUES = [
  "Your current visibility snapshot",
  "Why competitors show up (and the sources behind it)",
  "What to do next (30–60 day plan)",
  "What progress should look like (in Nobori)",
  "Impact target (direction)",
  "Why this matters",
];

const STRATEGY_BODY_TEXT_CLASS = "text-[15px] leading-7 text-[#171717]";
const MAX_TASK_EVIDENCE_ROWS = 12;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const extractDomain = (value?: string | null): string => {
  if (!value) return "";
  try {
    const normalized = value.startsWith("http") ? value : `https://${value}`;
    return new URL(normalized).hostname.replace(/^www\./i, "");
  } catch {
    return String(value)
      .replace(/^https?:\/\//i, "")
      .replace(/^www\./i, "")
      .split(/[/?#:]/)[0];
  }
};

const FREE_PUBLISH_DOMAINS = ["medium.com", "dev.to", "substack.com"];
const COMMUNITY_EVIDENCE_PATTERN =
  /community|discussion|forum|thread|reddit|quora|stack\s*overflow|stackexchange|q\s*&\s*a|\bqa\b|\bqna\b/i;
const COMMUNITY_PATH_PATTERN =
  /\/(r\/[^/]+\/comments|questions?|answers?|qna|forums?|community|communities|discussion|discussions|thread|threads|topic|topics)(\/|$)/i;
const YOUTUBE_HOSTS = ["youtube.com", "youtu.be"];

const isPreferredFreePublishUrl = (value?: string | null): boolean => {
  const domain = extractDomain(value);
  if (!domain) return false;
  return FREE_PUBLISH_DOMAINS.some(
    (preferredDomain) =>
      domain === preferredDomain || domain.endsWith(`.${preferredDomain}`),
  );
};

const normalizeTypeLabel = (value?: string | null): string =>
  toUrlTypeLabel(value);

const normalizeTaskType = (value?: string | null): string =>
  String(value || "")
    .trim()
    .toLowerCase();

const isFreePublishTaskType = (value?: string | null): boolean => {
  const normalized = normalizeTaskType(value);
  return normalized === "freepublish" || normalized === "free publish";
};

const normalizeTaskEvidenceType = (value?: string | null): string => {
  const normalized = String(value || "").trim();
  return normalized || "Other";
};

const isUnknownTaskEvidenceType = (value?: string | null): boolean => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  return !normalized || normalized === "other";
};

const toTitleCaseLabel = (value?: string | null): string => {
  const normalized = String(value || "")
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();
  if (!normalized) return "Other";
  return normalized
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const UUID_PATTERN =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

const UNORDERED_MARKER_RE = /^\s*[-•*–—−]\s+/;
const ORDERED_MARKER_RE = /^\s*\d+[.)]\s+/;
const stripListMarkerPrefix = (line: string): string => {
  let value = String(line || "");
  let prev = "";
  while (value !== prev) {
    prev = value;
    value = value.replace(/^\s*[-•*–—−]+\s+/, "");
    while (/^\s*(?:[-•*–—−]|\d+[.)])/.test(value)) {
      value = value.replace(/^\s*(?:[-•*–—−]|\d+[.)])\s*/, "");
    }
  }
  return value.trim();
};

const normalizeTaskText = (value?: string | null): string => {
  const input = String(value || "");
  if (!input.trim()) return "";
  return input
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\n")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/avg position:\s*[\d.]+\s*/gi, "")
    .replace(/average position:\s*[\d.]+\s*/gi, "")
    .replace(/\(\s*maps?\s+to\s+(?:[0-9a-f-]{36}\s*(?:,\s*)?)+\)/gi, "")
    .replace(/\brelated\s+prompts?\s*:\s*(?:[0-9a-f-]{36}\s*(?:,\s*)?)+/gi, "")
    .replace(UUID_PATTERN, "")
    .split(/\r?\n/)
    .map((line) =>
      line
        .replace(/^(\s*[-*]\s*)#{1,6}\s*/g, "$1")
        .replace(/^(\s*\d+\.\s*)#{1,6}\s*/g, "$1")
        .replace(/^\s{0,3}#{1,6}\s*/g, "")
        .trimEnd(),
    )
    .join("\n")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const normalizeOriginalTaskValue = (value: unknown) =>
  String(value ?? "").trim();

const hasOriginalStrategyTaskCopy = (task: StrategyPlanTask) => {
  const original = task.original;
  if (!original) return false;
  return (["title", "description", "rationale", "impact"] as const).some(
    (field) => {
      const originalValue = normalizeOriginalTaskValue(original[field]);
      if (!originalValue) return false;
      return originalValue !== normalizeOriginalTaskValue(task[field]);
    },
  );
};

const getStrategyTaskDisplayCopy = (
  task: StrategyPlanTask,
  showOriginal: boolean,
): StrategyPlanTask => {
  if (!showOriginal || !hasOriginalStrategyTaskCopy(task) || !task.original) {
    return task;
  }
  return {
    ...task,
    title: normalizeOriginalTaskValue(task.original.title) || task.title,
    description:
      normalizeOriginalTaskValue(task.original.description) || task.description,
    rationale:
      normalizeOriginalTaskValue(task.original.rationale) || task.rationale,
    impact: normalizeOriginalTaskValue(task.original.impact) || task.impact,
  };
};

const renderTaskInlineMarkdown = (text: string, keyPrefix = "task-inline") => {
  const source = String(text || "");
  if (!source) return null;

  const parts: React.ReactNode[] = [];
  const regex =
    /(\[([^\]]+)\]\(([^)]+)\)|`([^`]+)`|\*\*([^*]+)\*\*|__([^_]+)__|\*([^*\n]+)\*|_([^_\n]+)_|((https?:\/\/|www\.)[^\s)]+))/g;
  let lastIndex = 0;
  let tokenIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(source)) !== null) {
    const [full] = match;
    const start = match.index;
    if (start > lastIndex) {
      parts.push(source.slice(lastIndex, start));
    }

    const markdownLabel = match[2];
    const markdownHref = match[3];
    const inlineCode = match[4];
    const boldAsterisk = match[5];
    const boldUnderscore = match[6];
    const italicAsterisk = match[7];
    const italicUnderscore = match[8];
    const autoUrl = match[9];

    if (markdownLabel && markdownHref) {
      const href = normalizeTaskUrl(markdownHref.trim());
      if (href) {
        parts.push(
          <a
            key={`${keyPrefix}-md-link-${start}-${tokenIndex}`}
            href={href}
            target="_blank"
            rel="noreferrer"
            className="text-[#0A66C2] underline decoration-[#BBD7F5] underline-offset-2 hover:text-[#004182] break-all"
          >
            {markdownLabel.trim() || href}
          </a>,
        );
      } else {
        parts.push(markdownLabel.trim());
      }
    } else if (inlineCode) {
      parts.push(
        <code
          key={`${keyPrefix}-code-${start}-${tokenIndex}`}
          className="mx-[1px] rounded bg-[#F5F5F5] px-1.5 py-0.5 font-mono text-[12px] text-[#3F3F46]"
        >
          {inlineCode}
        </code>,
      );
    } else if (boldAsterisk || boldUnderscore) {
      parts.push(
        <strong
          key={`${keyPrefix}-bold-${start}-${tokenIndex}`}
          className="font-semibold text-[#171717]"
        >
          {(boldAsterisk || boldUnderscore || "").trim()}
        </strong>,
      );
    } else if (italicAsterisk || italicUnderscore) {
      parts.push(
        <span
          key={`${keyPrefix}-italic-${start}-${tokenIndex}`}
          className="text-[#171717]"
        >
          {(italicAsterisk || italicUnderscore || "").trim()}
        </span>,
      );
    } else if (autoUrl) {
      const href = autoUrl.startsWith("http") ? autoUrl : `https://${autoUrl}`;
      parts.push(
        <a
          key={`${keyPrefix}-auto-link-${start}-${tokenIndex}`}
          href={href}
          target="_blank"
          rel="noreferrer"
          className="text-[#0A66C2] underline decoration-[#BBD7F5] underline-offset-2 hover:text-[#004182] break-all"
        >
          {autoUrl}
        </a>,
      );
    } else {
      parts.push(full);
    }

    lastIndex = start + full.length;
    tokenIndex += 1;
  }

  if (lastIndex < source.length) {
    parts.push(source.slice(lastIndex));
  }

  return parts;
};

const renderTaskMarkdown = (value?: string | null, keyPrefix = "task-copy") => {
  const normalized = normalizeTaskText(value);
  if (!normalized) return <span>-</span>;

  const lines = normalized.split("\n");
  const nodes: React.ReactNode[] = [];
  let i = 0;
  let orderedListNext = 1;
  const resolveOrderedStart = (firstMarker?: number) => {
    const marker = Number.isFinite(firstMarker as number)
      ? Number(firstMarker)
      : 1;
    if (marker === 1 && orderedListNext > 1) return orderedListNext;
    return marker > 0 ? marker : orderedListNext;
  };

  while (i < lines.length) {
    const line = String(lines[i] || "").trimEnd();
    if (!line.trim()) {
      i += 1;
      continue;
    }

    if (UNORDERED_MARKER_RE.test(line)) {
      const items: string[] = [];
      while (
        i < lines.length &&
        UNORDERED_MARKER_RE.test(String(lines[i] || ""))
      ) {
        const cleanedItem = stripListMarkerPrefix(String(lines[i] || ""));
        if (cleanedItem) items.push(cleanedItem);
        i += 1;
      }
      const numericItems = items
        .map((item) => item.match(/^\s*(\d+)[.)]\s+(.*)$/))
        .filter((match): match is RegExpMatchArray => Boolean(match));
      const shouldRenderAsOrdered =
        items.length > 0 && numericItems.length === items.length;
      if (shouldRenderAsOrdered) {
        const start = Number(numericItems[0][1]) || 1;
        const orderedItems = numericItems.map((match) => match[2].trim());
        orderedListNext = start + orderedItems.length;
        nodes.push(
          <ol
            key={`${keyPrefix}-ol-from-ul-${i}`}
            className={`list-decimal list-outside pl-5 space-y-1.5 ${STRATEGY_BODY_TEXT_CLASS}`}
            start={start}
          >
            {orderedItems.map((item, idx) => (
              <li
                key={`${keyPrefix}-ol-from-ul-item-${i}-${idx}`}
                className="leading-7"
              >
                {renderTaskInlineMarkdown(
                  item,
                  `${keyPrefix}-ol-from-ul-${idx}`,
                )}
              </li>
            ))}
          </ol>,
        );
        continue;
      }
      nodes.push(
        <ul
          key={`${keyPrefix}-ul-${i}`}
          className={`list-disc list-outside pl-5 space-y-1.5 ${STRATEGY_BODY_TEXT_CLASS}`}
        >
          {items.map((item, idx) => (
            <li key={`${keyPrefix}-ul-item-${i}-${idx}`} className="leading-7">
              {renderTaskInlineMarkdown(item, `${keyPrefix}-ul-${idx}`)}
            </li>
          ))}
        </ul>,
      );
      continue;
    }

    if (ORDERED_MARKER_RE.test(line)) {
      const items: string[] = [];
      let firstMarker: number | undefined;
      while (
        i < lines.length &&
        ORDERED_MARKER_RE.test(String(lines[i] || ""))
      ) {
        const markerMatch = String(lines[i] || "").match(/^\s*(\d+)[.)]\s+/);
        if (markerMatch && firstMarker === undefined) {
          firstMarker = Number(markerMatch[1]);
        }
        const cleanedItem = stripListMarkerPrefix(String(lines[i] || ""));
        if (cleanedItem) items.push(cleanedItem);
        i += 1;
      }
      const start = resolveOrderedStart(firstMarker);
      orderedListNext = start + items.length;
      nodes.push(
        <ol
          key={`${keyPrefix}-ol-${i}`}
          className={`list-decimal list-outside pl-5 space-y-1.5 ${STRATEGY_BODY_TEXT_CLASS}`}
          start={start}
        >
          {items.map((item, idx) => (
            <li key={`${keyPrefix}-ol-item-${i}-${idx}`} className="leading-7">
              {renderTaskInlineMarkdown(item, `${keyPrefix}-ol-${idx}`)}
            </li>
          ))}
        </ol>,
      );
      continue;
    }

    const paragraphLines: string[] = [];
    while (i < lines.length) {
      const candidate = String(lines[i] || "").trimEnd();
      if (!candidate.trim()) break;
      if (
        UNORDERED_MARKER_RE.test(candidate) ||
        ORDERED_MARKER_RE.test(candidate)
      ) {
        break;
      }
      paragraphLines.push(candidate.trim());
      i += 1;
    }
    const paragraph = paragraphLines.join(" ").trim();
    if (!paragraph) {
      i += 1;
      continue;
    }
    nodes.push(
      <p key={`${keyPrefix}-p-${i}`} className={STRATEGY_BODY_TEXT_CLASS}>
        {renderTaskInlineMarkdown(paragraph, `${keyPrefix}-p-${i}`)}
      </p>,
    );
  }

  return (
    <div className={`space-y-1.5 ${STRATEGY_BODY_TEXT_CLASS}`}>{nodes}</div>
  );
};

const normalizeTaskUrl = (value?: string | null): string => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^[a-z0-9.-]+\.[a-z]{2,}/i.test(raw)) return `https://${raw}`;
  return raw;
};

const normalizeEvidenceUrl = (value?: string | null): string => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^[a-z0-9.-]+\.[a-z]{2,}/i.test(raw)) return `https://${raw}`;
  return "";
};

const isYouTubeEvidenceEntry = (value: unknown): boolean => {
  if (typeof value === "string") {
    const normalizedUrl = normalizeEvidenceUrl(value);
    if (!normalizedUrl) return /youtube/i.test(value);
    try {
      const host = new URL(normalizedUrl).hostname
        .replace(/^www\./i, "")
        .toLowerCase();
      return YOUTUBE_HOSTS.some(
        (youtubeHost) =>
          host === youtubeHost || host.endsWith(`.${youtubeHost}`),
      );
    } catch {
      return false;
    }
  }
  if (!isRecord(value)) return false;
  const urlType = String(value.url_type || value.urlType || value.type || "")
    .trim()
    .toLowerCase();
  if (urlType.includes("youtube")) return true;
  return isYouTubeEvidenceEntry(
    String(value.url || value.source_url || value.href || value.loc || ""),
  );
};

const isCommunityEvidenceEntry = (value: unknown): boolean => {
  if (typeof value === "string") {
    const normalizedUrl = normalizeEvidenceUrl(value);
    if (!normalizedUrl) return COMMUNITY_EVIDENCE_PATTERN.test(value);
    try {
      const parsed = new URL(normalizedUrl);
      const host = parsed.hostname.replace(/^www\./i, "").toLowerCase();
      if (
        host === "reddit.com" ||
        host.endsWith(".reddit.com") ||
        host === "quora.com" ||
        host.endsWith(".quora.com") ||
        host === "stackoverflow.com" ||
        host.endsWith(".stackoverflow.com") ||
        host === "stackexchange.com" ||
        host.endsWith(".stackexchange.com")
      ) {
        return true;
      }
      if (
        host === "github.com" &&
        /\/(issues|discussions?)(\/|$)/i.test(parsed.pathname || "")
      ) {
        return true;
      }
      return COMMUNITY_PATH_PATTERN.test(parsed.pathname || "");
    } catch {
      return false;
    }
  }
  if (!isRecord(value)) return false;
  const domainType = String(value.domain_type || value.domainType || "")
    .trim()
    .toLowerCase();
  if (domainType.includes("community")) return true;
  const urlType = String(value.url_type || value.urlType || value.type || "")
    .trim()
    .toLowerCase();
  if (COMMUNITY_EVIDENCE_PATTERN.test(urlType)) return true;
  return isCommunityEvidenceEntry(
    String(value.url || value.source_url || value.href || value.loc || ""),
  );
};

const getFaviconSrc = (value?: string | null): string => {
  const normalized = normalizeTaskUrl(value);
  if (!normalized) return "";
  return `https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${encodeURIComponent(normalized)}&size=64`;
};

type StrategyPlacementCard = {
  url: string;
  domainLabel: string;
  matchType: string;
  price: string;
  categories: string;
  country: string;
  language: string;
};

const extractGuestPostPlacements = (
  evidence: unknown,
): StrategyPlacementCard[] => {
  if (!isRecord(evidence)) return [];
  const guestPostView = isRecord(evidence.guest_post_view)
    ? evidence.guest_post_view
    : null;
  const placements = Array.isArray(guestPostView?.placements)
    ? (guestPostView?.placements as unknown[])
    : [];
  return placements
    .map((item) => {
      if (!isRecord(item)) return null;
      const normalizedUrl = normalizeTaskUrl(
        String(item.url || item.domain || ""),
      );
      if (!normalizedUrl) return null;
      const categories = Array.isArray(item.categories)
        ? item.categories
            .map((entry) => String(entry || "").trim())
            .filter(Boolean)
            .slice(0, 4)
            .join(", ")
        : "";
      const matchType = toTitleCaseLabel(
        String(item.match_type || item.matchType || item.type || "Other"),
      );
      const country = String(item.country || item.country_code || "")
        .trim()
        .replace(/_/g, " ");
      const language = String(item.language || "").trim();
      const parseAmount = (value: unknown): number | null => {
        const num = Number(value);
        return Number.isFinite(num) && num > 0 ? num : null;
      };
      const formatAmount = (value: number): string =>
        String(Math.round(value * 100) / 100)
          .replace(/\.00$/, "")
          .replace(/(\.\d)0$/, "$1");
      const amount =
        parseAmount(item.price_eur) ??
        parseAmount(item.priceEur) ??
        parseAmount(item.price_amount) ??
        parseAmount(item.priceAmount) ??
        parseAmount(item.price) ??
        parseAmount(item.price_from) ??
        parseAmount(item.priceFrom);
      const currencyCodeRaw = String(
        item.currency_code || item.currencyCode || item.currency || "EUR",
      )
        .trim()
        .toUpperCase();
      const currencyCode = /^[A-Z]{3}$/.test(currencyCodeRaw)
        ? currencyCodeRaw
        : "EUR";
      const fallbackPrice =
        amount !== null ? `${currencyCode} ${formatAmount(amount)}` : "";
      const price =
        String(item.price_label || item.priceLabel || "").trim() ||
        fallbackPrice ||
        "N/A";
      return {
        url: normalizedUrl,
        domainLabel: extractDomain(normalizedUrl) || normalizedUrl,
        matchType: matchType || "Other",
        price,
        categories,
        country,
        language,
      } satisfies StrategyPlacementCard;
    })
    .filter((entry): entry is StrategyPlacementCard => Boolean(entry))
    .slice(0, 8);
};

const toEvidenceRow = (entry: unknown): PartialData | null => {
  if (typeof entry === "string") {
    const rawUrl = normalizeEvidenceUrl(entry);
    if (!rawUrl) return null;
    return {
      domain: extractDomain(rawUrl),
      url: rawUrl,
      type: "Other",
      used: 1,
      usedPercents: "1",
      typeDomain: "Other",
    } satisfies PartialData;
  }
  if (!isRecord(entry)) return null;
  const rawUrl = normalizeEvidenceUrl(
    String(entry.url || entry.source_url || entry.final_url || entry.loc || ""),
  );
  if (!rawUrl) return null;
  const usedRaw =
    Number(entry.used_total || entry.used || entry.citation_count_in_cluster) ||
    0;
  const usedValue = Number.isFinite(usedRaw) ? usedRaw : 0;
  const rawType = String(
    entry.url_type || entry.urlType || entry.type || "",
  ).trim();
  const rawDomainType = String(
    entry.domain_type || entry.domainType || "Other",
  ).trim();

  return {
    domain: extractDomain(rawUrl),
    url: rawUrl,
    type: normalizeTypeLabel(rawType),
    used: usedValue,
    usedPercents: `${usedValue}`,
    typeDomain: rawDomainType || "Other",
  } satisfies PartialData;
};

const collectEvidenceUrls = (
  value: unknown,
  collector: Map<string, { url: string; type?: string; used?: number }>,
) => {
  if (typeof value === "string") {
    const normalizedUrl = normalizeEvidenceUrl(value);
    if (!normalizedUrl) return;
    if (!collector.has(normalizedUrl)) {
      collector.set(normalizedUrl, {
        url: normalizedUrl,
      });
    }
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectEvidenceUrls(item, collector));
    return;
  }
  if (!isRecord(value)) return;

  const maybeUrl = value.url;
  if (typeof maybeUrl === "string" && maybeUrl.trim()) {
    const normalizedUrl = normalizeEvidenceUrl(maybeUrl.trim());
    if (normalizedUrl && !collector.has(normalizedUrl)) {
      const maybeType =
        typeof value.url_type === "string"
          ? value.url_type
          : typeof value.type === "string"
            ? value.type
            : undefined;
      const maybeUsed =
        typeof value.used_total === "number"
          ? value.used_total
          : typeof value.used === "number"
            ? value.used
            : undefined;
      collector.set(normalizedUrl, {
        url: normalizedUrl,
        type: maybeType,
        used: maybeUsed,
      });
    }
  }

  Object.values(value).forEach((nested) => {
    if (typeof nested === "object" && nested !== null) {
      collectEvidenceUrls(nested, collector);
    }
  });
};

const buildEvidenceRows = (task: StrategyPlanTask): PartialData[] => {
  const normalizedTaskType = normalizeTaskType(task.task_type);
  const hasExplicitTaskType = normalizedTaskType.length > 0;
  const taskText =
    `${task.title || ""} ${task.description || ""}`.toLowerCase();
  const isCommunityTask = hasExplicitTaskType
    ? normalizedTaskType.includes("community")
    : /community|reddit|quora|forum|thread|reply|discussion|q\s*&\s*a|\bqa\b/i.test(
        taskText,
      );
  const isYouTubeTask = hasExplicitTaskType
    ? normalizedTaskType.includes("youtube")
    : /\byoutube\b/i.test(taskText);
  const taskEvidence = isRecord(task.evidence) ? task.evidence : {};
  const guestPostView = isRecord(taskEvidence.guest_post_view)
    ? taskEvidence.guest_post_view
    : null;

  const missingPublisherPages = Array.isArray(
    guestPostView?.missing_publisher_pages,
  )
    ? guestPostView.missing_publisher_pages
    : Array.isArray(taskEvidence.missing_publisher_pages)
      ? taskEvidence.missing_publisher_pages
      : [];
  const missingCommunityPages = Array.isArray(
    taskEvidence.missing_community_pages,
  )
    ? taskEvidence.missing_community_pages
    : [];
  const communityUrls = Array.isArray(taskEvidence.community_urls)
    ? taskEvidence.community_urls
    : [];
  const evidenceUrls = Array.isArray(taskEvidence.urls)
    ? taskEvidence.urls
    : [];
  const sourceUrls = Array.isArray(taskEvidence.source_urls)
    ? taskEvidence.source_urls
    : [];
  const publisherUrls = Array.isArray(taskEvidence.publisher_urls)
    ? taskEvidence.publisher_urls
    : [];
  const outreachUrls = Array.isArray(taskEvidence.outreach_urls)
    ? taskEvidence.outreach_urls
    : [];
  const fallbackSourceRows = Array.isArray(taskEvidence.fallback_source_rows)
    ? taskEvidence.fallback_source_rows
    : [];
  const mergeRowsByUrl = (rows: PartialData[]) => {
    const byUrl = new Map<string, PartialData>();
    rows.forEach((row) => {
      const key = String(row.url || "")
        .trim()
        .toLowerCase();
      if (!key) return;
      const existing = byUrl.get(key);
      if (!existing) {
        byUrl.set(key, {
          ...row,
          type: normalizeTaskEvidenceType(row.type),
          typeDomain: normalizeTaskEvidenceType(row.typeDomain),
        });
        return;
      }
      const nextUsed = Number(row.used || 0);
      const existingUsed = Number(existing.used || 0);
      if (Number.isFinite(nextUsed) && nextUsed > existingUsed) {
        existing.used = nextUsed;
        existing.usedPercents = `${nextUsed}`;
      }
      if (
        isUnknownTaskEvidenceType(existing.type) &&
        !isUnknownTaskEvidenceType(row.type)
      ) {
        existing.type = normalizeTaskEvidenceType(row.type);
      }
      if (
        isUnknownTaskEvidenceType(existing.typeDomain) &&
        !isUnknownTaskEvidenceType(row.typeDomain)
      ) {
        existing.typeDomain = normalizeTaskEvidenceType(row.typeDomain);
      }
      if (!existing.domain && row.domain) {
        existing.domain = row.domain;
      }
    });
    return Array.from(byUrl.values());
  };

  if (
    ["guestpost", "ownedlisticle", "freepublish"].includes(normalizedTaskType)
  ) {
    const rows = mergeRowsByUrl(
      [
        ...missingPublisherPages,
        ...sourceUrls,
        ...publisherUrls,
        ...outreachUrls,
        ...evidenceUrls,
        ...fallbackSourceRows,
      ]
        .map((entry) => toEvidenceRow(entry))
        .filter((entry): entry is PartialData => Boolean(entry)),
    ).filter((entry) =>
      normalizedTaskType === "freepublish"
        ? isPreferredFreePublishUrl(entry.url)
        : true,
    );
    if (!rows.length) return [];
    return rows
      .slice()
      .sort((a, b) => Number(b.used || 0) - Number(a.used || 0))
      .slice(0, MAX_TASK_EVIDENCE_ROWS);
  }

  if (isCommunityTask) {
    const rowsFromMissing = missingCommunityPages
      .map((entry) => toEvidenceRow(entry))
      .filter((entry): entry is PartialData => Boolean(entry))
      .map((entry) => ({
        ...entry,
        type: entry.type || "Community",
        typeDomain: entry.typeDomain || "Community",
      }));
    const rowsFromCommunityUrls = communityUrls
      .map((url) =>
        toEvidenceRow({
          url,
          url_type: "community",
          domain_type: "Community",
          used: 1,
        }),
      )
      .filter((entry): entry is PartialData => Boolean(entry));
    const rowsFromEvidenceUrls = evidenceUrls
      .map((entry) => toEvidenceRow(entry))
      .filter((entry): entry is PartialData => Boolean(entry))
      .map((entry) => {
        const currentDomainType = String(entry.typeDomain || "").trim();
        const looksCommunity = isCommunityEvidenceEntry({
          url: entry.url,
          url_type: entry.type,
          domain_type: entry.typeDomain,
        });
        return {
          ...entry,
          typeDomain:
            currentDomainType || (looksCommunity ? "Community" : "Other"),
        };
      });
    const rowsFromSourceUrls = sourceUrls
      .map((entry) => toEvidenceRow(entry))
      .filter((entry): entry is PartialData => Boolean(entry))
      .filter((entry) =>
        isCommunityEvidenceEntry({
          url: entry.url,
          url_type: entry.type,
          domain_type: entry.typeDomain,
        }),
      );
    const directCommunityRows = mergeRowsByUrl([
      ...rowsFromMissing,
      ...rowsFromCommunityUrls,
      ...rowsFromSourceUrls,
      ...rowsFromEvidenceUrls.filter((entry) =>
        isCommunityEvidenceEntry({
          url: entry.url,
          url_type: entry.type,
          domain_type: entry.typeDomain,
        }),
      ),
    ]).slice(0, MAX_TASK_EVIDENCE_ROWS);
    if (directCommunityRows.length) return directCommunityRows;

    const fallbackEvidenceCommunityRows = fallbackSourceRows
      .map((entry) => toEvidenceRow(entry))
      .filter((entry): entry is PartialData => Boolean(entry))
      .filter((entry) =>
        isCommunityEvidenceEntry({
          url: entry.url,
          url_type: entry.type,
          domain_type: entry.typeDomain,
        }),
      );
    if (fallbackEvidenceCommunityRows.length) {
      return mergeRowsByUrl(fallbackEvidenceCommunityRows).slice(
        0,
        MAX_TASK_EVIDENCE_ROWS,
      );
    }
    return [];
  }

  if (isYouTubeTask) {
    const youtubeDirectRows = mergeRowsByUrl(
      [
        ...evidenceUrls,
        ...sourceUrls,
        ...communityUrls,
        ...missingCommunityPages,
      ]
        .map((entry) => toEvidenceRow(entry))
        .filter((entry): entry is PartialData => Boolean(entry))
        .filter((entry) =>
          isYouTubeEvidenceEntry({
            url: entry.url,
            url_type: entry.type,
            domain_type: entry.typeDomain,
          }),
        )
        .map((entry) => ({
          ...entry,
          type: entry.type || "YouTube",
          typeDomain: entry.typeDomain || "Community",
        })),
    );

    return youtubeDirectRows
      .slice()
      .sort((a, b) => Number(b.used || 0) - Number(a.used || 0))
      .slice(0, MAX_TASK_EVIDENCE_ROWS);
  }

  const collector = new Map<
    string,
    { url: string; type?: string; used?: number }
  >();
  collectEvidenceUrls(task.evidence, collector);
  const extracted = Array.from(collector.values())
    .slice(0, 5)
    .map((entry) => {
      const domain = extractDomain(entry.url);
      const usedValue = Number.isFinite(entry.used) ? Number(entry.used) : 1;
      return {
        domain,
        url: entry.url,
        type: normalizeTypeLabel(entry.type),
        used: usedValue,
        usedPercents: `${usedValue}`,
        typeDomain: "Other",
      } satisfies PartialData;
    });
  if (extracted.length) return extracted;
  const fallbackEvidenceRows = fallbackSourceRows
    .map((entry) => toEvidenceRow(entry))
    .filter((entry): entry is PartialData => Boolean(entry));
  if (fallbackEvidenceRows.length) {
    return mergeRowsByUrl(fallbackEvidenceRows).slice(
      0,
      MAX_TASK_EVIDENCE_ROWS,
    );
  }
  return [];
};

type TaskEvidenceColumnMode = "none" | "urlType" | "domainType";

const resolveTaskEvidenceColumnMode = (
  taskType?: string | null,
): TaskEvidenceColumnMode => {
  const normalized = normalizeTaskType(taskType);
  if (normalized === "guestpost" || normalized === "freepublish") {
    return "urlType";
  }
  if (normalized === "ownedlisticle") {
    return "domainType";
  }
  return "none";
};

const TaskEvidenceTable = ({
  rows,
  taskType,
}: {
  rows: PartialData[];
  taskType?: string | null;
}) => {
  if (!rows.length) {
    return (
      <div className="rounded-md border border-[#E5E5E5] bg-white px-3 py-2 text-sm text-[#737373]">
        <LocalizedText id="legacy.no.matching.evidence.sources.available.for.this.task.gngn8l" />
      </div>
    );
  }
  const mode = resolveTaskEvidenceColumnMode(taskType);
  const columns = React.useMemo<ColumnDef<PartialData>[]>(() => {
    const baseColumns: ColumnDef<PartialData>[] = [
      {
        accessorKey: "url",
        header: "URL",
        cell: ({ row }) => {
          const rawUrl = String(row.getValue("url") || "");
          const formattedUrl = rawUrl.startsWith("http")
            ? rawUrl
            : `https://${rawUrl}`;
          return (
            <div className="flex items-center gap-2 max-w-[700px] truncate overflow-hidden whitespace-nowrap">
              <img
                className="w-[20px] h-[20px]"
                src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${formattedUrl}&size=64`}
                alt=""
              />
              <a
                href={formattedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="truncate hover:underline hover:underline-offset-2"
              >
                {rawUrl}
              </a>
            </div>
          );
        },
      },
    ];

    if (mode === "urlType") {
      baseColumns.push({
        accessorKey: "type",
        header: "Type",
        cell: ({ row }) => {
          const type = String(row.getValue("type") || "Other");
          return (
            <div className="capitalize">
              <Badge
                className={getUrlTypeBadgeClassName(type)}
                variant="outline"
              >
                {toUrlTypeLabel(type)}
              </Badge>
            </div>
          );
        },
      });
    }

    if (mode === "domainType") {
      baseColumns.push({
        accessorKey: "typeDomain",
        header: "Domain Type",
        cell: ({ row }) => {
          const type = String(row.getValue("typeDomain") || "Other");
          return (
            <div className="capitalize">
              <Badge
                className={getDomainTypeBadgeClassName(type)}
                variant="outline"
              >
                {type}
              </Badge>
            </div>
          );
        },
      });
    }

    baseColumns.push({
      accessorKey: "used",
      header: () => (
        <div className="text-center">
          <LocalizedText id="legacy.used.yjrj1e" />
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex justify-center">
          {String(row.getValue("used") || 0)}
        </div>
      ),
    });

    return baseColumns;
  }, [mode]);
  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: { columnVisibility: {} },
  });

  return (
    <ExtendedTable
      loading={false}
      hiddenPagination={true}
      pageName="citedDomains"
      table={table}
      columns={citedDomainsColumns.length}
    ></ExtendedTable>
  );
};

type ScrollContainer = Window | HTMLElement;

const isWindowContainer = (container: ScrollContainer): container is Window =>
  container === window;

const getScrollContainer = (node: HTMLElement | null): ScrollContainer => {
  let current = node?.parentElement ?? null;
  while (current && current !== document.body) {
    const style = window.getComputedStyle(current);
    const canScrollY = /(auto|scroll|overlay)/.test(style.overflowY);
    if (canScrollY && current.scrollHeight > current.clientHeight) {
      return current;
    }
    current = current.parentElement;
  }
  return window;
};

const getContainerScrollTop = (container: ScrollContainer): number => {
  if (isWindowContainer(container)) {
    return container.scrollY;
  }
  return container.scrollTop;
};

export const StrategyContent = ({
  currentBrand,
  selectedCluster,
  snapshot,
  planTasks,
  isPlanLoading,
  isFullVisibilityCluster = false,
  isPdfExporting = false,
  onTasksSectionSeen,
}: StrategyProps) => {
  const { t } = useLocale();
  const [isOpen, setIsOpen] = React.useState(false);
  const [originalTaskIds, setOriginalTaskIds] = React.useState<Set<string>>(
    () => new Set(),
  );
  const item1Ref = useRef<HTMLDivElement | null>(null);
  const item2Ref = useRef<HTMLDivElement | null>(null);
  const item3Ref = useRef<HTMLDivElement | null>(null);
  const item4Ref = useRef<HTMLDivElement | null>(null);
  const item5Ref = useRef<HTMLDivElement | null>(null);
  const item6Ref = useRef<HTMLDivElement | null>(null);
  const tasksSectionSeenRef = useRef(false);

  const toggleOriginalTask = React.useCallback((taskId: string) => {
    setOriginalTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  }, []);

  const promptsData = React.useMemo(
    () =>
      (snapshot?.prompts || []).map((prompt) => ({
        id: prompt.prompt_id,
        text: prompt.prompt_text,
      })),
    [snapshot?.prompts],
  );

  const currentBrandDomain = React.useMemo(
    () => canonicalizeDomain(currentBrand?.url || ""),
    [currentBrand?.url],
  );

  const competitors = React.useMemo(
    () =>
      (snapshot?.competitors || [])
        .map((row) => ({
          domain: row.brand_domain || row.brand_name,
          position: row.avg_position,
          points: `${Math.round(Number(row.visibility || 0) * 10) / 10}%`,
          visibilityValue: Number.isFinite(Number(row.visibility))
            ? Number(row.visibility)
            : 0,
          isOwnBrand:
            Boolean(row.is_main) ||
            Boolean(
              currentBrandDomain &&
                canonicalizeDomain(row.brand_domain || "") ===
                  currentBrandDomain,
            ),
        }))
        .sort((left, right) => {
          if (right.visibilityValue !== left.visibilityValue) {
            return right.visibilityValue - left.visibilityValue;
          }
          const leftPosition = Number.isFinite(Number(left.position))
            ? Number(left.position)
            : Number.POSITIVE_INFINITY;
          const rightPosition = Number.isFinite(Number(right.position))
            ? Number(right.position)
            : Number.POSITIVE_INFINITY;
          if (leftPosition !== rightPosition) {
            return leftPosition - rightPosition;
          }
          return String(left.domain || "").localeCompare(
            String(right.domain || ""),
          );
        }),
    [currentBrandDomain, snapshot?.competitors],
  );

  const citedPagesRows = React.useMemo<PartialData[]>(
    () =>
      (snapshot?.cited_pages || []).map((item) => ({
        domain: item.domain || extractDomain(item.url),
        url: item.url,
        type: normalizeTypeLabel(item.url_type),
        used: Number(item.used_total || 0),
        usedPercents: `${Number(item.used_total || 0)}`,
        typeDomain: "Other",
      })),
    [snapshot?.cited_pages],
  );

  const citedDomainsRows = React.useMemo<PartialData[]>(
    () =>
      (snapshot?.cited_domains || []).map((item) => ({
        domain: item.domain,
        url: `https://${item.domain}`,
        type: item.domain_type,
        used: Number(item.used_total || 0),
        usedPercents: `${Number(item.used_total || 0)}`,
        typeDomain: item.domain_type,
      })),
    [snapshot?.cited_domains],
  );
  const [openItems, setOpenItems] = React.useState<string[]>([]);
  const [manuallyClosed, setManuallyClosed] = React.useState<string[]>([]);
  const visibleOpenItems = isPdfExporting
    ? STRATEGY_ACCORDION_ITEM_VALUES
    : openItems;
  const manuallyClosedRef = useRef<string[]>([]);
  const scrollDirRef = useRef<"down" | "up">("down");
  const hasScrolledDownRef = useRef(false);
  const scrollContainerRef = useRef<ScrollContainer>(window);
  const autoOpenedOnceRef = useRef<Set<string>>(new Set());
  const sectionRefs = React.useMemo(
    () => [item1Ref, item2Ref, item3Ref, item4Ref, item5Ref, item6Ref],
    [],
  );

  useEffect(() => {
    manuallyClosedRef.current = manuallyClosed;
  }, [manuallyClosed]);

  const autoOpenVisibleItems = React.useCallback(() => {
    const container = scrollContainerRef.current;
    const rootRect = isWindowContainer(container)
      ? { top: 0, bottom: window.innerHeight }
      : container.getBoundingClientRect();
    const itemsToOpen: string[] = [];

    sectionRefs.forEach((ref) => {
      const el = ref.current;
      if (!el) return;
      const value = el.dataset.value;
      if (!value) return;
      if (manuallyClosedRef.current.includes(value)) return;
      if (autoOpenedOnceRef.current.has(value)) return;

      const rect = el.getBoundingClientRect();
      const visibleTop = Math.max(rect.top, rootRect.top);
      const visibleBottom = Math.min(rect.bottom, rootRect.bottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const ratio = visibleHeight / Math.max(rect.height, 1);
      if (ratio < 0.22) return;

      autoOpenedOnceRef.current.add(value);
      itemsToOpen.push(value);
    });

    if (!itemsToOpen.length) return;
    setOpenItems((prev) => {
      const next = [...prev];
      itemsToOpen.forEach((value) => {
        if (!next.includes(value)) next.push(value);
      });
      return next;
    });
  }, [sectionRefs]);

  useEffect(() => {
    const firstSection =
      sectionRefs.find((ref) => ref.current)?.current || null;
    const container = getScrollContainer(firstSection);
    scrollContainerRef.current = container;

    let lastY = getContainerScrollTop(container);
    const handleScroll = () => {
      const currentY = getContainerScrollTop(container);
      if (currentY > lastY + 2) {
        hasScrolledDownRef.current = true;
      }
      scrollDirRef.current = currentY > lastY ? "down" : "up";
      lastY = currentY;
      autoOpenVisibleItems();
    };

    const target = isWindowContainer(container) ? window : container;
    target.addEventListener("scroll", handleScroll, { passive: true });

    handleScroll();
    return () => target.removeEventListener("scroll", handleScroll);
  }, [autoOpenVisibleItems, sectionRefs]);

  const toggleItem = (
    event: React.MouseEvent<HTMLDivElement>,
    value: string,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setOpenItems((prev) => {
      const isOpen = prev.includes(value);
      if (isOpen) {
        setManuallyClosed((closed) =>
          closed.includes(value) ? closed : [...closed, value],
        );
        return prev.filter((v) => v !== value);
      }

      setManuallyClosed((closed) => closed.filter((v) => v !== value));

      return [...prev, value];
    });
  };

  const handleOpenItemsChange = React.useCallback(
    (nextOpenItems: string[]) => {
      const closedItems = openItems.filter(
        (value) => !nextOpenItems.includes(value),
      );
      const reopenedItems = nextOpenItems.filter((value) =>
        manuallyClosedRef.current.includes(value),
      );

      if (closedItems.length) {
        setManuallyClosed((closed) => [
          ...new Set([...closed, ...closedItems]),
        ]);
      }
      if (reopenedItems.length) {
        setManuallyClosed((closed) =>
          closed.filter((value) => !reopenedItems.includes(value)),
        );
      }
      setOpenItems(nextOpenItems);
    },
    [openItems],
  );

  useEffect(() => {
    const container = scrollContainerRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (!hasScrolledDownRef.current) return;
          if (scrollDirRef.current !== "down") return;

          const el = entry.target as HTMLElement;
          const value = el.dataset.value;
          if (!value) return;
          if (manuallyClosedRef.current.includes(value)) return;
          if (autoOpenedOnceRef.current.has(value)) return;

          autoOpenedOnceRef.current.add(value);
          setOpenItems((prev) =>
            prev.includes(value) ? prev : [...prev, value],
          );
        });
      },
      {
        threshold: 0.15,
        rootMargin: "0px 0px -25% 0px",
        root: isWindowContainer(container) ? null : container,
      },
    );
    sectionRefs.forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, [sectionRefs]);

  useEffect(() => {
    tasksSectionSeenRef.current = false;
  }, [selectedCluster?.id]);

  useEffect(() => {
    setOriginalTaskIds(new Set());
  }, [selectedCluster?.id, planTasks]);

  useEffect(() => {
    const target = item3Ref.current;
    if (!target || !onTasksSectionSeen) return;
    const container = scrollContainerRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.15) return;
          if (tasksSectionSeenRef.current) return;
          tasksSectionSeenRef.current = true;
          onTasksSectionSeen();
        });
      },
      {
        threshold: [0.15, 0.4],
        root: isWindowContainer(container) ? null : container,
      },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [onTasksSectionSeen, selectedCluster?.id]);

  const citedPagesTable = useReactTable({
    data: citedPagesRows,
    columns: partialPromptsDetails,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      columnVisibility: {},
    },
  });

  const domainsTable = useReactTable({
    data: citedDomainsRows,
    columns: partialDomains,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      columnVisibility: {},
    },
  });

  const clusterFallbackMetrics = React.useMemo(() => {
    const prompts = Array.isArray(selectedCluster?.prompts)
      ? selectedCluster.prompts.filter(
          (prompt: any) => prompt?.is_active !== false,
        )
      : [];
    const visibilityValues = prompts
      .map((prompt) => Number(prompt?.visibility))
      .filter((value) => Number.isFinite(value));
    const positionValues = prompts
      .map((prompt) => Number(prompt?.position))
      .filter((value) => Number.isFinite(value) && value > 0);
    const visibility =
      visibilityValues.length > 0
        ? visibilityValues.reduce((sum, value) => sum + value, 0) /
          visibilityValues.length
        : null;
    const position =
      positionValues.length > 0
        ? positionValues.reduce((sum, value) => sum + value, 0) /
          positionValues.length
        : null;
    return { visibility, position };
  }, [selectedCluster]);

  const planTaskEntries = React.useMemo(() => {
    return (planTasks || [])
      .map((task) => ({
        task,
        evidenceRows: buildEvidenceRows(task),
      }))
      .filter(({ task, evidenceRows }) => {
        const normalizedTaskType = normalizeTaskType(task.task_type);
        const hasExplicitTaskType = normalizedTaskType.length > 0;
        const isCommunityLikeTask = hasExplicitTaskType
          ? normalizedTaskType.includes("community")
          : COMMUNITY_EVIDENCE_PATTERN.test(
              `${task.title || ""} ${task.description || ""}`,
            );
        const isYouTubeTask = hasExplicitTaskType
          ? normalizedTaskType.includes("youtube")
          : /\byoutube\b/i.test(
              `${task.title || ""} ${task.description || ""}`,
            );
        if (!isCommunityLikeTask && !isYouTubeTask) return true;
        return evidenceRows.length > 0;
      });
  }, [planTasks]);

  const avgVisibility = React.useMemo(() => {
    return clusterFallbackMetrics.visibility;
  }, [clusterFallbackMetrics.visibility]);
  const avgPosition =
    snapshot?.average_position ?? clusterFallbackMetrics.position;

  const formatAvg = (value: number | null, suffix = "") => {
    if (value === null || Number.isNaN(value)) return "--";
    const rounded = Math.round(value * 10) / 10;
    return `${rounded}${suffix}`;
  };

  const resolvedBrandId =
    (currentBrand as any)?.brandId || (currentBrand as any)?.id || null;
  const promptsPath = React.useMemo(() => {
    const params = new URLSearchParams();
    if (resolvedBrandId) params.set("brandId", resolvedBrandId);
    if (selectedCluster?.id) params.set("cluster", selectedCluster.id);
    const query = params.toString();
    return `/dashboard/prompts${query ? `?${query}` : ""}`;
  }, [resolvedBrandId, selectedCluster?.id]);

  const visibilityNow = Number.isFinite(Number(avgVisibility))
    ? Math.max(0, Math.min(100, Number(avgVisibility)))
    : 0;
  const visibilityTarget = Math.min(100, Math.max(visibilityNow + 20, 70));
  const visibilityBarsNow = Math.max(
    0,
    Math.min(20, Math.round(visibilityNow / 5)),
  );
  const visibilityBarsTarget = Math.max(
    visibilityBarsNow,
    Math.min(20, Math.round(visibilityTarget / 5)),
  );
  const positionNow =
    Number.isFinite(Number(avgPosition)) && Number(avgPosition) > 0
      ? Number(avgPosition)
      : 5;
  const positionTarget = Math.max(
    1,
    Math.round((positionNow - Math.max(positionNow * 0.35, 0.6)) * 10) / 10,
  );

  return (
    <div className="flex flex-col gap-5">
      <div className="flex gap-2">
        <Badge variant="outline" className="h-8 px-2 py-0">
          <Eye></Eye> <LocalizedText id="legacy.visibility.s925mr" />{" "}
          <div className="bg-[#FF5E001A] text-[#FF5E00] p-1 rounded-md">
            {formatAvg(avgVisibility, "%")}
          </div>
        </Badge>
        <Badge variant="outline" className="h-8 px-2 py-0">
          <ChartNoAxesColumnIncreasing />
          <LocalizedText id="legacy.position.1k9t5h8" />{" "}
          <div className="bg-[#FF5E001A] text-[#FF5E00] p-1 rounded-md">
            {formatAvg(avgPosition)}
          </div>
        </Badge>
      </div>
      <div className="text-[15px] leading-7 text-[#171717]">
        <LocalizedRichText
          id="strategyContent.hero.copy"
          values={{
            brand: (
              <span className="inline-flex items-baseline mx-1">
                <span className="relative top-[6px] flex aspect-square w-8 h-5 items-center justify-center rounded-lg">
                  <img
                    className="w-6 rounded-md border"
                    src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${canonicalizeDomain(currentBrand?.url) || ""}&size=64`}
                    alt={`${currentBrand?.name || "brand"} icon`}
                  />
                </span>
                <span className="font-semibold text-[#FF5E00] capitalize">
                  {currentBrand?.name}
                </span>
              </span>
            ),
            cluster: (
              <span className="text-[#FF5E00]">{selectedCluster?.title}</span>
            ),
          }}
        />
      </div>
      <div className="text-[15px] leading-7 text-[#171717]">
        <LocalizedText id="legacy.a.cluster.is.not.a.single.keyword.its.1un0f5n" />
      </div>
      <h4 className="font-semibold">
        <LocalizedText id="legacy.prompts.in.this.cluster.1pxcgvt" />
      </h4>
      <div className="relative border-t border-x rounded-md h-fit">
        <div className="text-sm p-2 border-b rounded-b-md w-full gap-2 ">
          <Collapsible
            open={isPdfExporting || isOpen}
            onOpenChange={setIsOpen}
            className=""
          >
            <div className="max-h-[101px] mb-[20px] overflow-x-auto flex flex-row gap-2 flex-wrap">
              {promptsData.slice(0, 3).length ? (
                promptsData.slice(0, 3).map((prompt: any, index: number) => {
                  return (
                    <Link
                      key={`${prompt.id || prompt.text}-${index}`}
                      to={promptsPath}
                      className="cursor-pointer flex items-center w-fit gap-1 bg-[#F5F5F5] text-[#737373] rounded-md p-1"
                    >
                      <div className=" underline decoration-dotted underline-offset-4 truncate max-w-[210px]">
                        {prompt.text}
                      </div>
                      <ArrowUpRight size={14} />
                    </Link>
                  );
                })
              ) : (
                <div>-</div>
              )}
              {promptsData.length > 3 && (
                <>
                  <CollapsibleContent className="flex flex-row gap-2 flex-wrap">
                    {promptsData.slice(3).map((prompt: any, index: number) => {
                      return (
                        <Link
                          key={`${prompt.id || prompt.text}-${index + 3}`}
                          to={promptsPath}
                          className="flex items-center w-fit gap-1 bg-[#F5F5F5] text-[#737373] rounded-md p-1"
                        >
                          <div className="cursor-pointer underline decoration-dotted underline-offset-4 truncate max-w-[210px]">
                            {prompt.text}
                          </div>
                          <ArrowUpRight size={14} />
                        </Link>
                      );
                    })}
                  </CollapsibleContent>
                  <div className="flex absolute -bottom-[16px] left-1/2 -translate-x-1/2 items-center justify-between gap-4 px-4">
                    <CollapsibleTrigger asChild>
                      <Button variant="outline" className="flex">
                        {isPdfExporting || isOpen ? (
                          <ChevronUp></ChevronUp>
                        ) : (
                          <ChevronDown></ChevronDown>
                        )}{" "}
                        <LocalizedText id="legacy.show.yjzr6u" />{" "}
                        {isPdfExporting || isOpen ? "less" : "more"}
                      </Button>
                    </CollapsibleTrigger>
                  </div>
                </>
              )}
            </div>
          </Collapsible>
        </div>
      </div>
      <div className="text-[15px] leading-7 text-[#171717]">
        <LocalizedText id="legacy.these.prompts.may.use.different.wording.but.they.1kts3w4" />
      </div>
      <h4 className="font-semibold">
        <LocalizedText id="legacy.how.ai.search.works.in.plain.english.geysn6" />
      </h4>
      <div className="text-[15px] leading-7 text-[#171717]">
        <LocalizedText id="legacy.ai.systems.do.not.rank.websites.the.way.6pa3vp" />
      </div>

      <div className="text-[15px] leading-7 text-[#171717]">
        <LocalizedText id="legacy.if.the.web.does.not.clearly.connect.your.137m9q8" />
      </div>

      <div className="text-[15px] leading-7 text-[#171717]">
        <LocalizedText id="legacy.that.is.why.ai.visibility.is.less.about.ykfc11" />
      </div>

      <Accordion
        type="multiple"
        value={visibleOpenItems}
        onValueChange={handleOpenItemsChange}
        className="space-y-2"
      >
        <AccordionItem
          ref={item1Ref}
          data-value="Your current visibility snapshot"
          className="px-4 py-6 border rounded-md bg-[#F5F5F5] transition-colors data-[state=open]:bg-white"
          value="Your current visibility snapshot"
        >
          <AccordionTrigger
            showChevron={false}
            className="text-xl py-0 after:content-none font-semibold hover:no-underline cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div
                className="cursor-pointer rounded-md bg-[#F5F5F5] p-2"
                onClick={(event) => {
                  toggleItem(event, "Your current visibility snapshot");
                }}
              >
                <ChevronRight
                  size={16}
                  className={`transition-transform duration-500 ${
                    visibleOpenItems.includes(
                      "Your current visibility snapshot",
                    )
                      ? "rotate-90"
                      : "rotate-0"
                  }`}
                />
              </div>
              <div>
                <LocalizedText id="legacy.your.current.visibility.snapshot.1j8fldj" />
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-4 pt-3 text-[15px] leading-7 text-[#171717]">
            <p>
              <LocalizedRichText
                id="strategyContent.visibility.score"
                values={{
                  score: (
                    <span className="text-[#FF5E00] font-semibold">
                      {formatAvg(avgVisibility, "%")}
                    </span>
                  ),
                }}
              />
            </p>
            <p>
              <LocalizedRichText
                id="strategyContent.visibility.position"
                values={{
                  position: (
                    <span className="text-[#FF5E00] font-semibold">
                      {formatAvg(avgPosition)}
                    </span>
                  ),
                }}
              />
            </p>
            <h4 className="text-sm font-semibold">
              <LocalizedText id="legacy.what.this.means.3m1q9p" />
            </h4>
            <p>
              <LocalizedText id="legacy.this.score.shows.how.often.your.brand.appears.1rneak2" />
            </p>

            <p>
              <LocalizedText id="legacy.a.low.score.means.that.even.if.your.1k8pit2" />
            </p>

            <p>
              <LocalizedText id="legacy.a.stronger.score.means.your.brand.is.already.5ib5ym" />
            </p>
            <h3 className="text-base font-semibold">
              <LocalizedText id="legacy.who.is.being.mentioned.instead.1cxjv8z" />
            </h3>
            <p>
              <LocalizedText id="legacy.across.this.cluster.the.platform.most.often.detected.13v8owd" />
            </p>

            <SimpleTable
              isRunResultLoading={false}
              data={competitors || []}
              mainColumnName="Brand"
              middleColumn="Position"
              hidden={true}
            ></SimpleTable>
            <h4 className="text-sm font-semibold">
              <LocalizedText id="legacy.why.this.matters.1mrghtj" />
            </h4>
            <p>
              <LocalizedText id="legacy.this.matters.because.this.cluster.represents.an.active.14im3ee" />
            </p>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem
          value={"Why competitors show up (and the sources behind it)"}
          ref={item2Ref}
          data-value="Why competitors show up (and the sources behind it)"
          className="px-4 py-6 border rounded-md bg-[#F5F5F5] transition-colors data-[state=open]:bg-white"
        >
          <AccordionTrigger
            showChevron={false}
            className="text-xl py-0 after:content-none font-semibold hover:no-underline cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div
                className="cursor-pointer rounded-md bg-[#F5F5F5] p-2"
                onClick={(event) => {
                  toggleItem(
                    event,
                    "Why competitors show up (and the sources behind it)",
                  );
                }}
              >
                <ChevronRight
                  size={16}
                  className={`transition-transform duration-500 ${
                    visibleOpenItems.includes(
                      "Why competitors show up (and the sources behind it)",
                    )
                      ? "rotate-90"
                      : "rotate-0"
                  }`}
                />
              </div>
              <div>
                <LocalizedText id="legacy.why.competitors.show.up.and.the.sources.behind.19qng7u" />
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-3 pt-3 text-[15px] leading-7 text-[#171717]">
            <p>
              <LocalizedText id="legacy.competitors.keep.getting.recommended.because.ai.platforms.see.z8216j" />
            </p>
            <p>
              <LocalizedText id="legacy.in.commercial.prompts.like.this.the.strongest.sources.rqz2yx" />
            </p>
            <ul className="pl-2 list-disc list-inside space-y-2">
              <li>
                <span className="font-semibold">
                  <LocalizedText id="legacy.listicles.13rj9z" />
                </span>{" "}
                <LocalizedText id="legacy.best.top.roundups.and.vendor.shortlists.1xfxwyc" />
              </li>
              <li>
                <span className="font-semibold">
                  <LocalizedText id="legacy.comparison.pages.d0pj30" />
                </span>{" "}
                <LocalizedText id="legacy.x.vs.y.and.best.for.pages.1j5lf4h" />
              </li>
              <li>
                <span className="font-semibold">
                  <LocalizedText id="legacy.directories.and.review.sites.1ro1of7" />
                </span>{" "}
                <LocalizedText id="legacy.category.pages.and.vendor.profiles.gq620f" />
              </li>
              <li>
                <span className="font-semibold">
                  <LocalizedText id="legacy.community.threads.vv2k3v" />
                </span>{" "}
                <LocalizedText id="legacy.real.people.recommending.tools.by.name.1avj00i" />
              </li>
              <li>
                <span className="font-semibold">
                  <LocalizedText id="legacy.videos.1g4eb9j" />
                </span>{" "}
                <LocalizedText id="legacy.best.tools.roundups.1nyavq2" />
              </li>
              <li>
                <span className="font-semibold">
                  <LocalizedText id="legacy.clear.product.pages.1w0dxvb" />
                </span>{" "}
                <LocalizedText id="legacy.pages.that.explain.who.the.product.is.for.hk3yud" />
              </li>
            </ul>
            <p>
              Below are the{" "}
              <span className="font-semibold">
                <LocalizedText id="legacy.exact.pages.ai.cited.98zfax" />
              </span>{" "}
              when answering prompts in this cluster. These are your
              highest-leverage targets — they’re shaping the shortlist today.
            </p>
            <h3 className="font-semibold text-base">
              <LocalizedText id="legacy.cited.pages.urls.the.exact.pages.ai.used.uksuqb" />
            </h3>
            <p>
              <LocalizedText id="legacy.these.are.the.specific.urls.ai.cited.when.13ku5w0" />
            </p>
            <ExtendedTable
              loading={false}
              hiddenPagination={true}
              pageName="strategy"
              table={citedPagesTable}
              columns={citedDomainsColumns.length}
            ></ExtendedTable>
            <h4 className="text-sm font-semibold">
              <LocalizedText id="legacy.how.to.read.this.table.181mso4" />
            </h4>
            <ul className="list-disc space-y-2 list-inside pl-3 marker:text-orange-500">
              <li>
                <span className="text-[#FF5E00] font-semibold">
                  <LocalizedText id="legacy.used.yjrj1e" />
                </span>{" "}
                <LocalizedText id="legacy.how.often.this.url.appeared.as.a.cited.1a5cibq" />
              </li>
              <li>
                <span className="text-[#FF5E00] font-semibold">
                  <LocalizedText id="legacy.type.yjwvwd" />
                </span>{" "}
                <LocalizedText id="legacy.what.kind.of.page.it.is.listicle.comparison.jglhk5" />
              </li>
            </ul>
            <Separator></Separator>
            <h3 className="font-semibold text-base">
              <LocalizedText id="legacy.cited.domains.the.sites.shaping.this.cluster.1glzoqr" />
            </h3>
            <p>
              <LocalizedText id="legacy.pages.are.evidence.domains.show.who.consistently.influences.eflrne" />
            </p>
            <ExtendedTable
              loading={false}
              hiddenPagination={true}
              pageName="citedDomains"
              table={domainsTable}
              columns={citedDomainsColumns.length}
            ></ExtendedTable>

            <h4 className="text-sm font-semibold">
              <LocalizedText id="legacy.how.to.read.this.table.181mso4" />
            </h4>
            <ul className="list-disc space-y-2 list-inside pl-3 marker:text-orange-500">
              <li>
                <span className="text-[#FF5E00] font-semibold">
                  <LocalizedText id="legacy.used.yjrj1e" />
                </span>{" "}
                <LocalizedText id="legacy.number.of.times.this.domain.is.cited.in.1w36nle" />
              </li>
              <li>
                <span className="text-[#FF5E00] font-semibold">
                  <LocalizedText id="legacy.domain.type.l6ah31" />
                </span>{" "}
                tells you how to approach each domain:
              </li>
            </ul>
            <div className="space-y-1 pl-3 text-sm">
              <p>
                <span className="font-semibold">
                  <LocalizedText id="legacy.publisher.other.5oq9y8" />
                </span>{" "}
                <LocalizedText id="legacy.outreach.is.possible.t3is2" />
              </p>
              <p>
                <span className="font-semibold">
                  <LocalizedText id="legacy.competitor.1fxojyd" />
                </span>{" "}
                <LocalizedText id="legacy.treat.as.what.to.beat.not.outreach.95vjwr" />
              </p>
              <p>
                <span className="font-semibold">
                  <LocalizedText id="legacy.community.spmv92" />
                </span>{" "}
                <LocalizedText id="legacy.engage.via.replies.1ufhgir" />
              </p>
              <p>
                <span className="font-semibold">
                  <LocalizedText id="legacy.owned.3llyk2" />
                </span>{" "}
                <LocalizedText id="legacy.improve.your.own.pages.1nr918b" />
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        {!isFullVisibilityCluster ? (
          <>
            <AccordionItem
              ref={item3Ref}
              data-value="What to do next (30–60 day plan)"
              data-onboarding-target="strategy-tasks-section-anchor"
              className="px-4 py-6 border rounded-md bg-[#F5F5F5] transition-colors data-[state=open]:bg-white"
              value={"What to do next (30–60 day plan)"}
            >
              <AccordionTrigger
                showChevron={false}
                className="text-xl py-0 after:content-none font-semibold hover:no-underline cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="cursor-pointer rounded-md bg-[#F5F5F5] p-2"
                    onClick={(event) => {
                      toggleItem(event, "What to do next (30–60 day plan)");
                    }}
                  >
                    <ChevronRight
                      size={16}
                      className={`transition-transform duration-500 ${
                        visibleOpenItems.includes(
                          "What to do next (30–60 day plan)",
                        )
                          ? "rotate-90"
                          : "rotate-0"
                      }`}
                    />
                  </div>
                  <div>
                    <LocalizedText id="legacy.what.to.do.next.30.60.day.plan.r7facg" />
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent
                className={`flex flex-col gap-3 pt-3 ${STRATEGY_BODY_TEXT_CLASS}`}
              >
                <p>
                  <LocalizedRichText
                    id="strategyContent.plan.copy"
                    values={{
                      cluster: (
                        <span className="font-semibold text-[#FF5E00]">
                          {selectedCluster?.title}
                        </span>
                      ),
                    }}
                  />
                </p>
                {planTaskEntries.length ? (
                  <>
                    {planTaskEntries.map(({ task, evidenceRows }, index) => {
                      const showOriginal = originalTaskIds.has(task.id);
                      const canShowOriginal = hasOriginalStrategyTaskCopy(task);
                      const displayTask = getStrategyTaskDisplayCopy(
                        task,
                        showOriginal,
                      );
                      const normalizedTaskType = normalizeTaskType(
                        task.task_type,
                      );
                      const guestPostPlacements =
                        normalizedTaskType === "guestpost"
                          ? extractGuestPostPlacements(task.evidence)
                          : [];
                      const showEvidenceBlock = !(
                        isFreePublishTaskType(task.task_type) &&
                        evidenceRows.length === 0
                      );
                      return (
                        <React.Fragment key={task.id}>
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <h3 className="text-base flex items-center gap-2 font-semibold">
                              {" "}
                              <Flag
                                size={16}
                                fill="#FF5E00"
                                className="text-[#FF5E00]"
                              />
                              <span className="font-semibold text-[#FF5E00]">
                                <LocalizedText
                                  id="strategyContent.priorityLabel"
                                  values={{ index: index + 1 }}
                                />
                              </span>{" "}
                              {displayTask.title || "Untitled task"}
                            </h3>
                            {canShowOriginal ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs text-[#737373]"
                                onClick={() => toggleOriginalTask(task.id)}
                              >
                                {showOriginal
                                  ? t("common.showTranslated")
                                  : t("common.showOriginal")}
                              </Button>
                            ) : null}
                          </div>
                          <div>
                            <span className="font-semibold">
                              <LocalizedText id="legacy.description.r2fl5v" />
                            </span>{" "}
                            <br></br>
                            {renderTaskMarkdown(
                              displayTask.description,
                              `desc-${task.id}`,
                            )}
                          </div>
                          {guestPostPlacements.length ? (
                            <div className="space-y-2">
                              <p>
                                <span className="font-semibold">
                                  <LocalizedText id="legacy.recommended.placements.from.collaborator.pro.xvbiq2" />
                                </span>
                              </p>
                              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                                {guestPostPlacements.map((placement) => (
                                  <div
                                    key={`${placement.url}-${placement.matchType}`}
                                    className="rounded-md border border-[#E5E5E5] bg-white p-3"
                                  >
                                    <div className="flex items-start justify-between gap-2">
                                      <a
                                        href={placement.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="group flex min-w-0 items-center gap-2"
                                        title={placement.url}
                                      >
                                        <img
                                          className="h-6 w-6 shrink-0 rounded-sm"
                                          src={getFaviconSrc(placement.url)}
                                          alt=""
                                        />
                                        <span className="max-w-[220px] truncate hover:underline">
                                          {placement.domainLabel}
                                        </span>
                                        <ArrowUpRight
                                          size={14}
                                          className="opacity-0 transition-opacity group-hover:opacity-100"
                                        />
                                      </a>
                                      <span className="shrink-0 rounded-full bg-[#FFF1E8] px-2 py-0.5 text-xs font-semibold text-[#A63E00]">
                                        {placement.price}
                                      </span>
                                    </div>
                                    {placement.categories ? (
                                      <p className="mt-2 text-xs text-[#737373]">
                                        {placement.categories}
                                      </p>
                                    ) : null}
                                    <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                                      <span className="rounded-full bg-[#F5F5F5] px-2 py-0.5 text-[#525252]">
                                        {placement.matchType}
                                      </span>
                                      {placement.country ? (
                                        <span className="rounded-full bg-[#F5F5F5] px-2 py-0.5 text-[#525252]">
                                          {placement.country}
                                        </span>
                                      ) : null}
                                      {placement.language ? (
                                        <span className="rounded-full bg-[#F5F5F5] px-2 py-0.5 text-[#525252]">
                                          {placement.language}
                                        </span>
                                      ) : null}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : null}
                          {showEvidenceBlock ? (
                            <>
                              <p>
                                <span className="font-semibold">
                                  <LocalizedText id="legacy.evidence.zbrzjq" />
                                </span>
                              </p>
                              <TaskEvidenceTable
                                rows={evidenceRows}
                                taskType={task.task_type}
                              />
                            </>
                          ) : null}
                          <div className="space-y-0.5">
                            <span className="font-semibold">
                              <LocalizedText id="legacy.impact.1a2cqzr" />
                            </span>
                            {renderTaskMarkdown(
                              displayTask.impact,
                              `impact-${task.id}`,
                            )}
                          </div>
                          {index < planTaskEntries.length - 1 ? (
                            <Separator></Separator>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </>
                ) : isPlanLoading ? (
                  <div className="flex flex-col gap-4">
                    {Array.from({ length: 3 }).map((_, idx) => (
                      <div
                        key={idx}
                        className="rounded-md border p-4 bg-white space-y-3"
                      >
                        <Skeleton className="h-5 w-[45%]" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-[92%]" />
                        <Skeleton className="h-28 w-full" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-2 text-[#737373]">
                    <Spinner
                      className="text-[#FF5E00]"
                      variant="circle"
                      size={16}
                    />
                    <span>
                      <LocalizedText id="legacy.plan.tasks.are.not.available.for.this.cluster.eaggqp" />
                    </span>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              ref={item4Ref}
              data-value="What progress should look like (in Nobori)"
              className="px-4 py-6 border rounded-md bg-[#F5F5F5] transition-colors data-[state=open]:bg-white"
              value={"What progress should look like (in Nobori)"}
            >
              <AccordionTrigger
                showChevron={false}
                className="text-xl py-0 after:content-none font-semibold hover:no-underline cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="cursor-pointer rounded-md bg-[#F5F5F5] p-2"
                    onClick={(event) => {
                      toggleItem(
                        event,
                        "What progress should look like (in Nobori)",
                      ); // close this item
                    }}
                  >
                    <ChevronRight
                      size={16}
                      className={`transition-transform duration-500 ${
                        visibleOpenItems.includes(
                          "What progress should look like (in Nobori)",
                        )
                          ? "rotate-90"
                          : "rotate-0"
                      }`}
                    />
                  </div>
                  <div>
                    <LocalizedText id="legacy.what.progress.should.look.like.in.nobori.1kljidi" />
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent
                className={`flex flex-col gap-3 pt-3 ${STRATEGY_BODY_TEXT_CLASS}`}
              >
                <p>
                  <LocalizedText id="legacy.as.you.execute.the.plan.you.should.see.19xwfgu" />
                </p>
                <ul className="pl-4 space-y-2 list-disc list-inside">
                  <li>
                    <span className="font-semibold">
                      {" "}
                      <LocalizedText id="legacy.visibility.increases.tgvwku" />{" "}
                    </span>
                    <LocalizedRichText
                      id="strategyContent.outcome.morePrompts"
                      values={{
                        brand: (
                          <span className="inline-flex items-baseline">
                            <span className="relative top-[6px] flex aspect-square w-8 h-6 items-center justify-center rounded-lg">
                              <img
                                className="w-6 rounded-md border"
                                src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${canonicalizeDomain(currentBrand?.url) || ""}&size=64`}
                                alt="logo"
                              />
                            </span>
                            <span className="font-semibold text-[#FF5E00] capitalize mr-1">
                              {currentBrand?.name}
                            </span>
                          </span>
                        ),
                      }}
                    />
                  </li>
                  <li>
                    <span className="font-semibold">
                      {" "}
                      <LocalizedText id="legacy.average.position.improves.1gx1pgs" />{" "}
                    </span>
                    <LocalizedRichText
                      id="strategyContent.outcome.betterPosition"
                      values={{
                        brand: (
                          <span className="inline-flex items-baseline">
                            <span className="relative top-[6px] flex aspect-square w-8 h-6 items-center justify-center rounded-lg">
                              <img
                                className="w-6 rounded-md border"
                                src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${canonicalizeDomain(currentBrand?.url) || ""}&size=64`}
                                alt="logo"
                              />
                            </span>
                            <span className="font-semibold text-[#FF5E00] capitalize mr-1">
                              {currentBrand?.name}
                            </span>
                          </span>
                        ),
                      }}
                    />
                  </li>

                  <li>
                    <span>
                      <LocalizedText id="legacy.more.domains.include.you.you.start.appearing.across.18mo9hb" />
                    </span>
                  </li>

                  <li>
                    <span>
                      <LocalizedText id="legacy.more.cited.pages.mention.you.more.of.the.15p62kp" />
                    </span>
                  </li>
                </ul>

                <p>
                  <LocalizedText id="legacy.current.benchmark.x1fgb" />
                </p>

                <ul className="pl-4 space-y-2 list-disc list-inside">
                  <li>
                    <span>
                      <LocalizedText id="legacy.visibility.8wig61" />{" "}
                      <span className="text-[#FF5E00]">
                        {formatAvg(avgVisibility, "%")}
                      </span>{" "}
                    </span>
                  </li>

                  <li>
                    <span>
                      <LocalizedText id="legacy.average.position.kz8db9" />{" "}
                      <span className="text-[#FF5E00]">
                        {formatAvg(avgPosition)}
                      </span>{" "}
                    </span>
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              ref={item5Ref}
              data-value="Impact target (direction)"
              className="px-4 py-6 border rounded-md bg-[#F5F5F5] transition-colors data-[state=open]:bg-white"
              value={"Impact target (direction)"}
            >
              <AccordionTrigger
                showChevron={false}
                className="text-xl py-0 after:content-none font-semibold hover:no-underline cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="cursor-pointer rounded-md bg-[#F5F5F5] p-2"
                    onClick={(event) => {
                      toggleItem(event, "Impact target (direction)");
                    }}
                  >
                    <ChevronRight
                      size={16}
                      className={`transition-transform duration-500 ${
                        visibleOpenItems.includes("Impact target (direction)")
                          ? "rotate-90"
                          : "rotate-0"
                      }`}
                    />
                  </div>
                  <div>
                    <LocalizedText id="legacy.impact.target.direction.qa407m" />
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-3">
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <div className="rounded-md border bg-white p-6 space-y-3">
                    <div className="flex items-center gap-3 font-semibold">
                      <Eye size={24} strokeWidth={1.3} />
                      <LocalizedText id="legacy.visibility.s925mr" />
                    </div>
                    <div className="text-2xl font-semibold flex justify-between">
                      {formatAvg(visibilityNow, "%")}{" "}
                      <span className="text-[#737373] font-normal px-6 flex items-center">
                        <img src={arrow}></img>
                      </span>{" "}
                      <span className="text-[#FF5E00]">
                        {formatAvg(visibilityTarget, "%")}
                      </span>
                    </div>
                    <div className="grid grid-cols-20 gap-2">
                      {Array.from({ length: 20 }).map((_, index) => {
                        const activeNow = index < visibilityBarsNow;
                        const activeTarget = index < visibilityBarsTarget;
                        return (
                          <div
                            key={`visibility-bar-${index}`}
                            className={`h-11 rounded-sm ${
                              activeNow
                                ? "bg-[#FF5E00]"
                                : activeTarget
                                  ? "bg-[#FFD8C2]"
                                  : "bg-[#E5E5E5]"
                            }`}
                          />
                        );
                      })}
                    </div>
                  </div>
                  <div className="rounded-md border bg-white p-6 space-y-3">
                    <div className="relative flex justify-between overflow-hidden">
                      <div className="relative z-10 flex flex-col justify-between">
                        <div className="absolute flex items-center gap-2 font-semibold">
                          <ChartNoAxesColumnIncreasing size={18} />
                          <LocalizedText id="legacy.position.1k9t5h8" />
                        </div>
                        <div className="text-2xl flex items-end h-full pr-5 text-right font-semibold text-black/90">
                          {formatAvg(positionNow)}{" "}
                        </div>
                      </div>
                      <div className="flex min-h-[150px] min-w-0 flex-1 items-end px-2">
                        <svg
                          className="h-[112px] w-full max-h-[112px]"
                          viewBox="0 0 224 150"
                          preserveAspectRatio="none"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden
                        >
                          <path
                            d="M221 2.56028C157.5 -0.191396 129.5 125.132 56 124.351H2.5V149.789H221V2.56028Z"
                            fill="url(#positionCurveFill)"
                          />
                          <path
                            d="M2.5 124.226H56C129.5 124.226 157.5 -0.202545 221 2.54455"
                            stroke="url(#positionCurveStroke)"
                            strokeWidth="5"
                            strokeLinecap="round"
                          />
                          <defs>
                            <linearGradient
                              id="positionCurveFill"
                              x1="111.75"
                              y1="2.51562"
                              x2="111.75"
                              y2="149.789"
                              gradientUnits="userSpaceOnUse"
                            >
                              <stop stopColor="#FF5E00" stopOpacity="0.3" />
                              <stop
                                offset="1"
                                stopColor="#FF5E00"
                                stopOpacity="0"
                              />
                            </linearGradient>
                            <linearGradient
                              id="positionCurveStroke"
                              x1="111.75"
                              y1="2.5"
                              x2="111.75"
                              y2="124.229"
                              gradientUnits="userSpaceOnUse"
                            >
                              <stop stopColor="#FF5E00" />
                              <stop offset="1" stopColor="#FF9354" />
                            </linearGradient>
                          </defs>
                        </svg>
                      </div>
                      <div className="z-10 ml-3 flex flex-col items-end gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-semibold text-[#FF6A00]">
                            {formatAvg(positionTarget)}
                          </span>
                          <div className="w-[45px] h-[18px] bg-[#FF6A00] rounded-lg" />
                        </div>

                        {[2, 3, 4, 5].map((i) => (
                          <div
                            key={i}
                            className="w-[45px] h-[18px] bg-[#D9D9D9] rounded-lg"
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              ref={item6Ref}
              data-value="Why this matters"
              className="last:border-b-1 px-4 py-6 border rounded-md bg-[#F5F5F5] transition-colors data-[state=open]:bg-white"
              value={"Why this matters"}
            >
              <AccordionTrigger
                showChevron={false}
                className="text-xl py-0 after:content-none font-semibold hover:no-underline cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="cursor-pointer rounded-md bg-[#F5F5F5] p-2"
                    onClick={(event) => {
                      toggleItem(event, "Why this matters");
                    }}
                  >
                    <ChevronRight
                      size={16}
                      className={`transition-transform duration-500 ${
                        visibleOpenItems.includes("Why this matters")
                          ? "rotate-90"
                          : "rotate-0"
                      }`}
                    />
                  </div>
                  <div>
                    <LocalizedText id="legacy.why.this.matters.j00e3x" />
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent
                className={`flex flex-col gap-3 pt-3 ${STRATEGY_BODY_TEXT_CLASS}`}
              >
                <p className="block items-center flex-wrap">
                  <span>
                    <LocalizedText id="legacy.buyers.are.already.using.ai.platforms.to.shortlist.psrork" />
                  </span>
                  <span className="inline-flex items-baseline mx-1">
                    <span className="relative top-[6px] flex aspect-square w-8 h-4 items-center justify-center rounded-lg">
                      <img
                        className="w-6 rounded-md border"
                        src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${canonicalizeDomain(currentBrand?.url) || ""}&size=64`}
                        alt="logo"
                      />
                    </span>
                    <span className="font-semibold text-[#FF5E00] capitalize">
                      {currentBrand?.name}
                    </span>
                  </span>
                  <span className="break-words">
                    <LocalizedText id="legacy.is.missing.from.the.commercial.clusters.that.matter.1iiey11" />
                  </span>
                </p>
                <p className="block items-center flex-wrap">
                  <span>
                    <LocalizedText id="legacy.the.goal.isnt.more.content.the.goal.is.wizzn0" />
                  </span>
                  <span className="inline-flex items-baseline mx-1">
                    <span className="relative top-[6px] flex aspect-square w-8 h-5 items-center justify-center rounded-lg">
                      <img
                        className="w-6 rounded-md border"
                        src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${canonicalizeDomain(currentBrand?.url) || ""}&size=64`}
                        alt="logo"
                      />
                    </span>
                    <span className="font-semibold text-[#FF5E00] capitalize">
                      {currentBrand?.name}
                    </span>
                  </span>
                  <span>
                    <LocalizedText id="legacy.belongs.in.19kkm1o" />
                  </span>
                  <span className="ml-1 font-semibold text-[#FF5E00] capitalize">
                    {selectedCluster?.title}
                  </span>
                </p>
              </AccordionContent>
            </AccordionItem>
          </>
        ) : null}
      </Accordion>
    </div>
  );
};
